import React from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import HowItWorks from '../../components/HowItWorks';

const FAQS = [
  { q: "Why doesn't my invoice match my delivery weight?", a: "It depends on how that customer trades. For fixed-price SKU supply to retail multiples, the invoice carries the PO quantity within an agreed tolerance, an off-PO invoice triggers short-pays and AP query queues, so matching the PO is what gets you paid on time. For per-kg trade, the invoice carries the actual catch weight recorded at dispatch. Keystone knows which mode each customer is on and raises the right invoice automatically, while the operations ledger records the actual weights and batches in both cases." },
  { q: 'What is catch weight invoicing?', a: "Catch weight is the actual weight of a variable-weight product recorded at dispatch, a wheel of cheese, a meat primal, a box of fish. Catch weight invoicing bills the customer for that actual weight, and it's the standard for per-kg trade. Keystone captures catch weights per batch at dispatch and invoices per-kg customers at actual weight; fixed-price retail SKUs are invoiced at PO quantity instead, with the weight variance recorded and reportable." },
  { q: 'How do I invoice when I dispatched more than the PO quantity?', a: "Check the customer's trading terms, Keystone applies them automatically. Per-kg customer: invoice the actual dispatched weight, e.g. 108kg. Fixed-price PO from a retail multiple: invoice the 100kg PO quantity within the agreed tolerance, with the +8kg variance recorded as a first-class number in the operations layer, reportable, filterable and visible in your margin numbers, never silently lost." },
  { q: "Do I lose traceability if the invoice doesn't show the real weight?", a: "No, the opposite. Traceability lives in Keystone's operations layer, which records every gram, unit and batch you actually dispatched. That is your BRCGS / IFS Food traceability / EU food safety evidence. The invoice is a commercial document that follows the customer's trading terms; the trace evidence is operational. Keeping the two aligned but separate is standard practice, Keystone just automates it." },
  { q: 'Which accounting packages does this work with?', a: 'AccountsIQ out of the box, with Xero, Sage and QuickBooks available as scoped integrations. Invoices go across the API at the quantity each customer trades on, no CSV exports, no manual re-keying, no Monday-morning reconciliation spreadsheet.' },
];

/**
 * /site/how-it-works
 *
 * Dedicated page for the "PO → dispatch → invoice" workflow story. Reuses the
 * <HowItWorks compact={false} /> component (adds detail rows to every step)
 * and wraps it with SEO metadata + a longer "why the split matters" narrative.
 *
 * SEO intent: capture buyers searching for the operational fit before ever
 * hitting the pricing page, "how does food manufacturing software work",
 * "food ERP dispatch workflow", "PO to invoice food software".
 */
export default function SiteHowItWorks() {
  return (
    <SiteLayout>
      <SEO
        title="How Keystone works, catch-weight PO to invoice in 5 steps"
        description="The two invoicing modes food producers actually run: PO-quantity invoicing for fixed-price retail SKUs, actual catch-weight invoicing for per-kg trade, with the operations layer recording real weights and batches in both."
        path="/site/how-it-works"
        keywords="catch weight invoicing software, variable weight invoicing, invoice does not match delivery weight, catch weight ERP food, PO tolerance invoicing, food manufacturing workflow, sales order to invoice, PO to invoice food software, AccountsIQ integration food, Xero food manufacturer"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }}
      />

      {/* HERO */}
      <section style={{ background: PALETTE.cream, padding: '160px 32px 60px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 20,
          }}>How it works</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(44px, 6vw, 78px)',
            fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em',
            color: PALETTE.navy, margin: 0,
          }}>
            The daily rhythm.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>
              From PO to invoice.
            </em>
          </h1>
          <p style={{
            fontSize: 20, lineHeight: 1.55, color: PALETTE.slate,
            marginTop: 24, maxWidth: 700, fontWeight: 400,
          }}>
            Five steps between the retailer PO and the accurate invoice. Built for catch-weight food where 100 kg ordered never leaves the factory as exactly 100 kg.
          </p>
        </div>
      </section>

      {/* Full flow with detail rows */}
      <HowItWorks compact={false} showCta={false} />

      {/* Deep-dive narrative: why the PO/actual split matters */}
      <section style={{ background: '#fff', padding: '110px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>The differentiator</div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(28px, 3.6vw, 42px)',
            fontWeight: 700, color: PALETTE.navy, margin: 0,
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            Why food producers need an operations layer that records <em style={{ fontStyle: 'italic', color: PALETTE.magenta, fontWeight: 500 }}>more</em> than the invoice shows.
          </h2>

          <div style={{
            marginTop: 32, fontSize: 16.5, lineHeight: 1.7, color: PALETTE.slate,
          }}>
            <p style={{ margin: '0 0 20px' }}>
              A 100 kg cheese PO ships as 104 kg, 108 kg or 96 kg. Not sloppy, that’s variable-weight food. The industry calls the actual weight <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>catch weight</strong>. Handling it properly is where generic ERPs fail.
            </p>
            <p style={{ margin: 0 }}>
              Two invoicing modes, both standard. <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>Per-kg trade</strong> (wholesale, foodservice, export): invoice the actual catch weight. <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>Fixed-price retail SKUs</strong>: invoice the PO quantity within tolerance, the retailer’s AP matches on PO. Get it wrong and either your traceability breaks or the retailer short-pays every invoice.
            </p>
            <p style={{
              marginTop: 24, padding: '20px 24px', background: PALETTE.cream,
              borderLeft: `4px solid ${PALETTE.magenta}`, fontSize: 16,
            }}>
              <strong style={{ color: PALETTE.navy, fontWeight: 700, display: 'block', marginBottom: 6 }}>
                Keystone: one workflow, mode-aware invoicing.
              </strong>
              Operations layer records the real 108 kg, your traceability spine. The invoice, pushed to AccountsIQ / Xero / Sage over API, follows each customer’s terms: PO quantity for retail SKUs, catch weight for per-kg. The 8 kg variance stays a first-class number, filterable, reportable, visible in margin.
            </p>
            <p style={{
              marginTop: 20, fontSize: 16, lineHeight: 1.7, color: PALETTE.slate,
            }}>
              And it gets easier the more you use it. The quantities, weights and units a given customer expects are <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>learned from real orders and applied automatically</strong> next time, with the operator free to override whenever a day is different. <em style={{ color: PALETTE.navy }}>Keystone adapts to your factory, not the other way round.</em>
            </p>
          </div>

          <div style={{ marginTop: 48, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/site/pricing" data-testid="hiw-pricing-cta" style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '14px 26px',
              background: PALETTE.navy, color: '#fff',
              border: 'none', borderRadius: 6,
              fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', textDecoration: 'none',
            }}>See pricing →</Link>
            <Link to="/site/contact" data-testid="hiw-contact-cta" style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '14px 26px',
              background: 'transparent', color: PALETTE.navy,
              border: `1px solid ${PALETTE.navy}`, borderRadius: 6,
              fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', textDecoration: 'none',
            }}>Book a 20-min walkthrough →</Link>
          </div>
        </div>
      </section>

      {/* Catch-weight FAQ, mirrors the FAQPage JSON-LD above */}
      <section style={{ background: PALETTE.cream, padding: '90px 32px', borderTop: `1px solid ${PALETTE.border}` }} data-testid="hiw-faq">
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>Catch-weight FAQs</div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(26px, 3.2vw, 38px)',
            fontWeight: 700, color: PALETTE.navy, margin: '0 0 36px',
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            The questions producers actually ask.
          </h2>
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`hiw-faq-${i + 1}`} style={{
              background: '#fff', border: `1px solid ${PALETTE.border}`, borderRadius: 8,
              padding: '22px 26px', marginBottom: 14,
            }}>
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 19, fontWeight: 700, color: PALETTE.navy,
                margin: '0 0 10px', letterSpacing: '-0.01em',
              }}>{f.q}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: PALETTE.slate, margin: 0 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}
